import { useState, useMemo } from 'react';
import { ChevronsUpDown, UserCircle2, X, User, Bot, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { cn } from '@/lib/utils';
import { useUsers } from '@/hooks/use-users';
import { useRoleMap, resolveRoles } from '@/hooks/use-roles';
import { useAgents } from '@/hooks/use-agents';
import { useTeams } from '@/hooks/use-teams';

export interface AssigneeValue {
  type: 'user' | 'agent' | 'team';
  id: string;
  name: string;
}

export type AssigneeType = AssigneeValue['type'];

interface AssigneePickerProps {
  selected: AssigneeValue | null;
  onChange: (value: AssigneeValue | null) => void;
  allowUnassign?: boolean;
  disabled?: boolean;
  className?: string;
  /** Compact trigger for inline use (e.g. task header) */
  compact?: boolean;
  /**
   * Restrict which assignee groups render in the picker. Defaults to all
   * three — task assignment passes ['user','team'] to hide agents; the
   * rule editor uses the default.
   */
  allowedTypes?: AssigneeType[];
}

const ALL_ASSIGNEE_TYPES: AssigneeType[] = ['user', 'agent', 'team'];

function getInitials(firstName: string, lastName: string): string {
  return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
}

function getAvatarUrl(userId: string): string {
  return `/api/users/${userId}/avatar`;
}

const TYPE_ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  user: User,
  agent: Bot,
  team: Users,
};

/**
 * Popover-less variant — renders only the inner Command list (search,
 * group headings, avatars, role names). Embed this inside your own
 * popover/dialog when you want the same picker UX without the trigger
 * button (e.g. tasks FilterBar's per-filter editor).
 *
 * `onSelect` fires with the picked assignee; `onUnassign` fires when
 * the user picks the "Unassign" row (rendered only when `allowUnassign`
 * is true and `selected` is non-null). Neither manages popover state —
 * the consumer closes its own popover.
 *
 * `AssigneePicker` reuses this component internally so both surfaces
 * stay visually identical.
 */
export function AssigneePickerList({
  selected,
  onSelect,
  onUnassign,
  allowUnassign = false,
  allowedTypes = ALL_ASSIGNEE_TYPES,
  rolesEnabled = true,
}: {
  selected: AssigneeValue | null;
  onSelect: (value: AssigneeValue) => void;
  onUnassign?: () => void;
  allowUnassign?: boolean;
  allowedTypes?: AssigneeType[];
  /**
   * Defer the /roles fetch until the list is visible. AssigneePicker
   * passes `open` through so role names only load when the popover
   * opens; standalone consumers default to true.
   */
  rolesEnabled?: boolean;
}): React.ReactElement {
  const allowUsers = allowedTypes.includes('user');
  const allowAgents = allowedTypes.includes('agent');
  const allowTeams = allowedTypes.includes('team');
  const { data: usersData } = useUsers();
  const roleMap = useRoleMap({ enabled: rolesEnabled });
  const { data: agentsData } = useAgents();
  const { data: teamsData } = useTeams();

  const userOptions = useMemo(() => {
    if (!usersData?.items) return [];
    return usersData.items
      .filter((u) => u.rowStatus === 0)
      .map((u) => ({
        type: 'user' as const,
        id: u.id,
        name: `${u.firstName} ${u.lastName}`,
        initials: getInitials(u.firstName, u.lastName),
        roleName: resolveRoles(u.roleIds, roleMap)[0]?.name,
      }));
  }, [usersData?.items, roleMap]);

  const agentOptions = useMemo(() => {
    if (!agentsData) return [];
    return agentsData
      .filter((a) => a.isActive)
      .map((a) => ({ type: 'agent' as const, id: a.id, name: a.name }));
  }, [agentsData]);

  const teamOptions = useMemo(() => {
    if (!teamsData) return [];
    return teamsData.map((t) => ({ type: 'team' as const, id: t.id, name: t.name }));
  }, [teamsData]);

  const isSelected = (type: string, id: string): boolean =>
    selected?.type === type && selected?.id === id;
  const handleRowSelect = (type: 'user' | 'agent' | 'team', id: string, name: string): void => {
    onSelect({ type, id, name });
  };

  return (
    <Command
      filter={(value, search) => (value.toLowerCase().includes(search.toLowerCase()) ? 1 : 0)}
    >
      <CommandInput placeholder="Search users, agents, teams..." />
      <CommandList>
        <CommandEmpty>No results found.</CommandEmpty>

        {allowUnassign && selected && onUnassign && (
          <>
            <CommandGroup>
              <CommandItem onSelect={onUnassign}>
                <X className="mr-2 h-4 w-4" />
                Unassign
              </CommandItem>
            </CommandGroup>
            <CommandSeparator />
          </>
        )}

        {allowUsers && userOptions.length > 0 && (
          <CommandGroup heading="Users">
            {userOptions.map((option) => (
              <CommandItem
                key={`user-${option.id}`}
                value={`${option.name} user`}
                onSelect={() => handleRowSelect('user', option.id, option.name)}
                className={cn(
                  isSelected('user', option.id) && 'bg-primary/10 font-medium text-primary'
                )}
              >
                <Avatar size="sm" className="mr-2">
                  <AvatarImage src={getAvatarUrl(option.id)} />
                  <AvatarFallback>{option.initials}</AvatarFallback>
                </Avatar>
                <div className="flex min-w-0 flex-col">
                  <span className="truncate text-sm">{option.name}</span>
                  {option.roleName && (
                    <span className="truncate text-xs text-muted-foreground">
                      {option.roleName}
                    </span>
                  )}
                </div>
              </CommandItem>
            ))}
          </CommandGroup>
        )}

        {allowAgents && agentOptions.length > 0 && (
          <CommandGroup heading="AI Agents">
            {agentOptions.map((option) => (
              <CommandItem
                key={`agent-${option.id}`}
                value={`${option.name} agent`}
                onSelect={() => handleRowSelect('agent', option.id, option.name)}
                className={cn(
                  isSelected('agent', option.id) && 'bg-primary/10 font-medium text-primary'
                )}
              >
                <Bot className="mr-2 h-4 w-4 shrink-0 text-muted-foreground" />
                <span className="truncate">{option.name}</span>
              </CommandItem>
            ))}
          </CommandGroup>
        )}

        {allowTeams && teamOptions.length > 0 && (
          <CommandGroup heading="Teams">
            {teamOptions.map((option) => (
              <CommandItem
                key={`team-${option.id}`}
                value={`${option.name} team`}
                onSelect={() => handleRowSelect('team', option.id, option.name)}
                className={cn(
                  isSelected('team', option.id) && 'bg-primary/10 font-medium text-primary'
                )}
              >
                <Users className="mr-2 h-4 w-4 shrink-0 text-muted-foreground" />
                <span className="truncate">{option.name}</span>
              </CommandItem>
            ))}
          </CommandGroup>
        )}
      </CommandList>
    </Command>
  );
}

export function AssigneePicker({
  selected,
  onChange,
  allowUnassign = false,
  disabled = false,
  className,
  compact = false,
  allowedTypes = ALL_ASSIGNEE_TYPES,
}: AssigneePickerProps): React.ReactElement {
  const [open, setOpen] = useState(false);
  // Selected initials are needed for the trigger avatar fallback. Pull
  // them from the same useUsers() hook the list uses.
  const { data: usersData } = useUsers();
  const selectedInitials = useMemo(() => {
    if (selected?.type !== 'user') return null;
    const u = usersData?.items.find((x) => x.id === selected.id);
    return u ? getInitials(u.firstName, u.lastName) : null;
  }, [selected, usersData?.items]);

  const SelectedIcon = selected ? (TYPE_ICONS[selected.type] ?? User) : UserCircle2;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant={compact ? 'ghost' : 'outline'}
          size={compact ? 'sm' : 'default'}
          type="button"
          className={cn(compact ? 'h-8 gap-1.5 px-2 text-xs' : 'w-full justify-between', className)}
          disabled={disabled}
        >
          {selected ? (
            <span className="flex items-center gap-2 truncate">
              {selected.type === 'user' ? (
                <Avatar size="sm">
                  <AvatarImage src={getAvatarUrl(selected.id)} />
                  <AvatarFallback>{selectedInitials ?? '??'}</AvatarFallback>
                </Avatar>
              ) : (
                <SelectedIcon className="h-4 w-4 shrink-0 text-muted-foreground" />
              )}
              <span className={cn('truncate', compact && 'max-w-24')}>{selected.name}</span>
            </span>
          ) : (
            <span className="flex items-center gap-2 text-muted-foreground">
              <UserCircle2 className="h-4 w-4" />
              <span>{compact ? 'Unassigned' : 'Select assignee...'}</span>
            </span>
          )}
          <ChevronsUpDown
            className={cn('shrink-0 opacity-50', compact ? 'h-3 w-3' : 'ml-2 h-4 w-4')}
          />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[280px] p-0" align="start">
        <AssigneePickerList
          selected={selected}
          onSelect={(next) => {
            // Match prior behavior: clicking the already-selected row
            // closes without firing onChange.
            if (selected?.type === next.type && selected?.id === next.id) {
              setOpen(false);
              return;
            }
            onChange(next);
            setOpen(false);
          }}
          onUnassign={() => {
            onChange(null);
            setOpen(false);
          }}
          allowUnassign={allowUnassign}
          allowedTypes={allowedTypes}
          rolesEnabled={open}
        />
      </PopoverContent>
    </Popover>
  );
}
